import { DeleteOutlined, EditOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { THEME } from '@common/constants/theme.constant';
import { Button } from '@common/general-ui/button/button.component';
import { Field, IFieldProps } from '@common/general-ui/field/field.component';
import { Icon } from '@common/general-ui/icon/icon.component';
import { Image } from '@common/general-ui/image/image.component';
import { Readonly } from '@common/general-ui/readonly.component';
import { t } from '@common/hooks/use-t.hook';
import { Upload } from 'antd';
import { RcFile } from 'antd/lib/upload/interface';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { css } from 'twin.macro';

type TFile = string | RcFile | Blob;

export interface IUploadChangeArgs {
    target: {
        value: TFile;
        url: string;
    };
}

export type IUploadImageChangeHandler = (e: IUploadChangeArgs) => void;

export interface IUploadImageProps<TName = string> extends IFieldProps<RcFile | string, TName> {
    className?: string;
    onChange?: IUploadImageChangeHandler;
    onDelete?: () => void;
    onBlur?: (e: React.FocusEvent<unknown>) => void;
}

// eslint-disable-next-line react/display-name
export const UploadImage: React.FC<IUploadImageProps> = (props): JSX.Element => {
    const { onChange, onDelete } = props;
    const [loading, setLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState<RcFile | string | undefined>(props.value);

    useEffect(() => {
        setImageUrl(props.value);
    }, [props.value]);

    const _renderLoading = <div>{loading ? <Icon icon={LoadingOutlined} /> : <Icon icon={PlusOutlined} />}</div>;

    const handleDelete = useCallback(() => {
        setImageUrl(undefined);
        onDelete && onDelete();
    }, [onDelete]);

    const handleChange = useCallback<IUploadImageChangeHandler>(
        (e) => {
            setLoading(true);
            setImageUrl(e.target.url);
            onChange && onChange(e);
            // TODO move to async
            setLoading(false);
        },
        [onChange],
    );

    const handleReplace = useCallback<IUploadImageChangeHandler>(
        (e) => {
            if (e.target.url) {
                setImageUrl(e.target.url);
                onChange && onChange(e);
            }
        },
        [onChange],
    );

    const renderActions = (): JSX.Element => {
        return (
            <div css={styles.actions}>
                <Button name="delete" type="button" variant="empty" tw="mr-2 bg-transparent" onClick={handleDelete}>
                    <Icon icon={DeleteOutlined} tw="text-3xl text-white" />
                </Button>
                <UploadInternal onChange={handleReplace}>
                    <Button name="edit" type="button" variant="empty" tw="bg-transparent">
                        <Icon icon={EditOutlined} tw="text-3xl text-white" />
                    </Button>
                </UploadInternal>
            </div>
        );
    };
    return (
        <Field {...props}>
            <div tw="relative">
                {props.readOnly && <Readonly />}
                <div css={styles.content}>
                    {imageUrl ? (
                        <>
                            {renderActions()}
                            <Image css={styles.image} src={imageUrl} />
                        </>
                    ) : (
                        <UploadInternal onChange={handleChange}>
                            <div>+ {t('Upload new image')}</div>
                        </UploadInternal>
                    )}
                </div>{' '}
            </div>
        </Field>
    );
};

const UploadInternal = ({
    children,
    onChange,
}: {
    children: ReactNode;
    onChange: IUploadImageChangeHandler;
}): JSX.Element => {
    return (
        <Upload
            css={styles.antUpload}
            name="avatar"
            listType="picture-card"
            showUploadList={false}
            customRequest={(options): void => {
                const url = URL.createObjectURL(options.file as unknown as Blob);
                onChange && onChange({ target: { value: options.file, url } });
            }}
        >
            {children}
        </Upload>
    );
};

const styles = {
    content: css`
        position: relative;
        height: 240px;
        border: 1px solid ${THEME.LIGHT_GRAY};
        border-radius: 4px;
        display: flex;
        justify-content: center;
    `,
    actions: css`
        position: absolute;
        display: flex;
        z-index: 1;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: black;
        cursor: pointer;
        font-size: 20px;
        padding: 2px;
    `,
    antUpload: css`
        && .ant-upload {
            width: 100%;
            height: 100%;
            background-color: transparent;
            margin: 0;
            padding: 0;
            border: 0;
        }
    `,
    image: css`
        max-width: 100%;
        height: auto;
    `,
};
